









import { Component, Vue, Watch } from "vue-property-decorator";
import * as debounce from "debounce";
import MainHeader from "../components/MainHeader.vue";
import MainFooter from "../components/MainFooter.vue";
import ElInput from "../components/el-input.vue";
import ElSelect from "../components/el-select.vue";
import ElTextarea from "../components/el-textarea.vue";
import { elValidate } from "../models/validation/espressoValidation";
import ElUpload from "../components/el-upload.vue";
import ElDate from "../components/el-date.vue";
import { UserService } from "../models/user.service";
import { UserAddress } from "../models/userAddress.entity";
import { UserPersonalInfo } from "../models/userPersonalInfo.entity";
import { UserProfessionalInfo } from "../models/userProfessionalInfo.entity";
import getPostalCodeInfo from "../data/postal_code_info";

const VueScrollTo = require("vue-scrollto");
const genders = require("../data/genders.json");
const maritalStatus = require("../data/maritalStatus.json");
const brazilianStates = require("../data/brazilianStates.json");

@Component({
	name: "edit-info",
	components: {
		MainHeader,
		MainFooter,
		ElInput,
		ElSelect,
		ElUpload,
		ElDate,
		ElTextarea,
	},
})
export default class EditInfo extends Vue {
	professions = [];

	validationErrors = {
		userAddress: [],
		userPersonalInfo: [],
		userProfessionalInfo: [],
	};

	userAddress: UserAddress = new UserAddress();

	userPersonalInfo: UserPersonalInfo = new UserPersonalInfo();

	userProfessionalInfo: UserProfessionalInfo = new UserProfessionalInfo();

	userHasResidency: boolean = false;

	async mounted() {
		const registrationOptions = await UserService.getRegistrationOptions();
		this.professions = registrationOptions.professions;
	}

	async saveInfo() {
		if (await this.validateForm()) {
			try {
				await UserService.updatePersonalInfo(this.userPersonalInfo);
				await UserService.updateAddress(this.userAddress);
				await UserService.updateProfessionalInfo(this.userProfessionalInfo);
				await UserService.finishRegistration();
				this.$swal({ icon: "info", title: "Sucesso", text: "Você está inscrito no processo seletivo" });
				this.$router.push("/");
			} catch (error) {
				this.$swal({ icon: "error", title: "Houve um erro", text: "Tente novamente mais tarde." });
				console.error(error);
			}
		}
	}

	async validateForm() {
		this.validationErrors.userPersonalInfo = await elValidate(this.userPersonalInfo);
		this.validationErrors.userProfessionalInfo = await elValidate(this.userProfessionalInfo);

		if (this.validationErrors.userPersonalInfo.length || this.validationErrors.userProfessionalInfo.length) {
			this.$swal({ icon: "error", title: this.$t("validationError") as string, text: this.$t("checkErrorsAndTryAgain") as string });
			return false;
		}
		return true;
	}

	getProfessionsOptions() {
		return this.professions.map(profession => ({ label: profession.name, code: profession.id }));
	}

	getSpecialtiesOptions() {
		const profession = this.professions.find(professionOption => professionOption.id === this.userProfessionalInfo.profession);
		if (profession) {
			return profession.specialties.map(specialty => ({ label: specialty.name, code: specialty.id }));
		}
		return [];
	}

	getBrazilianStatesOptions() {
		return brazilianStates.map(state => ({ code: state.code, label: `${state.name} - ${state.code}` }));
	}

	getGendersOptions() {
		return genders.map(gender => ({ ...gender }));
	}

	getMaritalStatusOptions() {
		return maritalStatus.map(status => ({ ...status }));
	}

	getProfessionalDocumentName() {
		if (!this.userProfessionalInfo.profession) {
			return "Documento";
		}
		return this.professions.find(profession => profession.id === this.userProfessionalInfo.profession).documentName;
	}

	hasProfessionalDocumentAttachment() {
		if (!this.userProfessionalInfo.profession) {
			return false;
		}
		return !!this.professions.find(profession => profession.id === this.userProfessionalInfo.profession).documentAttachmentName;
	}

	hasError(entity: string, property: string) {
		const propertyError = this.validationErrors[entity].find(err => err.property === property);
		return propertyError ? propertyError.error : null;
	}

	@Watch("userAddress.postalCode")
	async autocompleteAddress(postalCode: string) {
		if (!postalCode || postalCode.length !== 9) {
			return;
		}

		const postalCodeInfo = await getPostalCodeInfo(postalCode);
		if (!postalCodeInfo) {
			this.$swal({ icon: "error", title: "CEP não encontrado", text: "Verifique se o seu CEP está correto." });
			return;
		}

		this.$set(this.userAddress, "street", postalCodeInfo.logradouro);
		this.$set(this.userAddress, "district", postalCodeInfo.bairro);
		this.$set(this.userAddress, "city", postalCodeInfo.localidade);
		this.$set(this.userAddress, "state", postalCodeInfo.uf);
	}

	@Watch("userAddress", { deep: true })
	async validateUserAddress(userAddress: UserAddress) {
		if (this.validationErrors.userAddress.length) {
			this.validationErrors.userAddress = await elValidate(this.userAddress);
		}
	}

	// eslint-disable-next-line func-names
	debouncedUpdatePersonalInfo = debounce((payload: any) => {
		UserService.updatePersonalInfo(payload)
			.catch(error => console.error(error));
	}, 1000);

	@Watch("userPersonalInfo", { deep: true })
	async validateUserPersonalInfo(userPersonalInfo: UserPersonalInfo) {
		const validationErrors = await elValidate(this.userPersonalInfo);
		if (this.validationErrors.userPersonalInfo.length) {
			this.validationErrors.userPersonalInfo = validationErrors;
		}

		// send properties that passed validation
		const payload = Object.assign({}, userPersonalInfo);
		validationErrors.forEach((error) => {
			delete payload[error.property];
		});
		this.debouncedUpdatePersonalInfo(payload);
	}

	@Watch("userProfessionalInfo", { deep: true })
	async validateUserProfessionalInfo(userProfessionalInfo: UserProfessionalInfo) {
		if (this.validationErrors.userProfessionalInfo.length) {
			this.validationErrors.userProfessionalInfo = await elValidate(this.userProfessionalInfo);
		}
	}
}
